import { useQuery } from '@tanstack/react-query';
import { useFetch } from '@/shared/hooks/useFetch';
import { useMutate } from '@/shared/hooks/useMutate';
import { queryKeys } from '@/shared/api/queryKeys';
import {
  apiReturnCancel,
  apiReturnConfirm,
  apiReturnReject,
  apiReturnsList,
  apiReturnsPendingCount,
} from '@/entities/returns/modules/api/returns';
import type { ApiReturnsListParams } from '@/shared/types/api/returns';

export const useReturns = (params: ApiReturnsListParams) =>
  useFetch({
    api: () => apiReturnsList(params),
    keywords: queryKeys.returns.list(params),
    staleTime: 30_000,
  });

export const useReturnsPendingCount = (enabled = true) =>
  useQuery({
    queryKey: queryKeys.returns.pendingCount,
    queryFn: apiReturnsPendingCount,
    enabled,
    refetchInterval: false,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });

export const useReturnConfirm = () =>
  useMutate({
    api: (id: number) => apiReturnConfirm(id),
    // A returned parcel no longer counts toward any PINFL limit.
    invalidateKeys: [queryKeys.returns.all, queryKeys.returns.pendingCount, queryKeys.pinfl.all],
  });

export const useReturnReject = () =>
  useMutate({
    api: ({ id, reject_reason }: { id: number; reject_reason?: string }) =>
      apiReturnReject(id, reject_reason),
    invalidateKeys: [queryKeys.returns.all, queryKeys.returns.pendingCount],
  });

export const useReturnCancel = () =>
  useMutate({
    api: (id: number) => apiReturnCancel(id),
    invalidateKeys: [queryKeys.returns.all, queryKeys.returns.pendingCount, queryKeys.pinfl.all],
  });
